import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  CreateDemandForecastParams,
  DEMAND_FORECAST_CADENCE,
  DEMAND_FORECAST_CADENCE_DISPLAY_NAMES,
  DEMAND_FORECAST_CADENCE_VALUES,
  DemandForecastCadence,
  HydratedDemandForecast,
  ITEM_UNIT_LABEL,
  ItemUnit,
  ItemUnitEnum,
  MAX_OPPORTUNITY_PROBABILITY,
  MIN_OPPORTUNITY_PROBABILITY,
  UpdateDemandForecastParams,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/CurrencyInputDecimal";
import { Input } from "@/components/ui/input";
import { ItemSelector } from "@/components/ItemSelector";
import { Label } from "@/components/ui/label";
import { LocationSelector } from "@/components/LocationSelector";
import { OpportunitySelector } from "@/components/OpportunitySelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "lucide-react";
import { toast } from "sonner";
import { formatDateInput, parseDateInput } from "./utils";
import { useItemsStore } from "@/modules/items/itemsStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { getItemUnitOptions } from "@/modules/items/itemUnitOptions";

interface DemandForecastFormData {
  opportunityId: string | null;
  locationId: string | null;
  itemId: string | null;
  description: string;
  reasoning: string;
  quantityPerPeriod: string;
  quantityUnit: ItemUnit;
  unitPrice: string;
  cadence: DemandForecastCadence;
  startDate: string;
  endDate: string;
}

function ForecastFormRow({
  label,
  htmlFor,
  required,
  children,
  wide,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-4">
        <Label
          htmlFor={htmlFor}
          className="shrink-0 text-sm font-medium leading-tight"
        >
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
        <div className={wide ? "w-[320px] shrink-0" : "w-[200px] shrink-0"}>
          {children}
        </div>
      </div>
    </div>
  );
}

const getInitialFormData = (
  fixedOpportunityId?: string,
): DemandForecastFormData => ({
  opportunityId: fixedOpportunityId ?? null,
  locationId: null,
  itemId: null,
  description: "",
  reasoning: "",
  quantityPerPeriod: "",
  quantityUnit: ItemUnitEnum.each,
  unitPrice: "0",
  cadence: DEMAND_FORECAST_CADENCE.MONTHLY,
  startDate: formatDateInput(new Date()),
  endDate: "",
});

const getFormDataFromForecast = (
  forecast: HydratedDemandForecast,
): DemandForecastFormData => ({
  opportunityId: forecast.opportunityId,
  locationId: forecast.locationId,
  itemId: forecast.itemId,
  description: forecast.description,
  reasoning: forecast.reasoning ?? "",
  quantityPerPeriod: forecast.quantityPerPeriod,
  quantityUnit: forecast.quantityUnit,
  unitPrice: forecast.unitPrice,
  cadence: forecast.cadence,
  startDate: formatDateInput(forecast.startDate),
  endDate: formatDateInput(forecast.endDate),
});

type DemandForecastEditorSheetProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: "create";
      demandForecast?: undefined;
      fixedOpportunityId?: string;
      readOnly?: boolean;
      onSave: (data: CreateDemandForecastParams) => Promise<true | string>;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: "edit";
      demandForecast: HydratedDemandForecast;
      fixedOpportunityId?: string;
      readOnly?: boolean;
      onSave: (data: UpdateDemandForecastParams) => Promise<true | string>;
    };

export function DemandForecastEditorSheet({
  open,
  onOpenChange,
  mode,
  demandForecast,
  fixedOpportunityId,
  readOnly = false,
  onSave,
}: DemandForecastEditorSheetProps) {
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const [formData, setFormData] = useState<DemandForecastFormData>(
    getInitialFormData(fixedOpportunityId),
  );
  const itemUnitOptions = getItemUnitOptions(
    epicorPrimitiveSupportEnabled,
    formData.quantityUnit,
  );
  const [opportunityProbabilityDraft, setOpportunityProbabilityDraft] =
    useState("");
  const [isSaving, setIsSaving] = useState(false);
  const items = useItemsStore((state) => state.items);
  const opportunities = useOpportunitiesStore((state) => state.opportunities);
  const updateOpportunity = useOpportunitiesStore(
    (state) => state.updateOpportunity,
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormData(
      mode === "edit" && demandForecast
        ? getFormDataFromForecast(demandForecast)
        : getInitialFormData(fixedOpportunityId),
    );
    setOpportunityProbabilityDraft(
      mode === "edit" && demandForecast?.opportunity
        ? String(demandForecast.opportunity.probability)
        : "",
    );
  }, [open, mode, demandForecast, fixedOpportunityId]);

  const selectedOpportunity = useMemo(() => {
    if (!formData.opportunityId) {
      return undefined;
    }
    if (
      mode === "edit" &&
      demandForecast?.opportunityId === formData.opportunityId &&
      demandForecast.opportunity
    ) {
      return demandForecast.opportunity;
    }
    return opportunities.get(formData.opportunityId);
  }, [demandForecast, formData.opportunityId, mode, opportunities]);

  useEffect(() => {
    if (!open || !selectedOpportunity) {
      return;
    }
    setOpportunityProbabilityDraft(String(selectedOpportunity.probability));
  }, [
    open,
    selectedOpportunity,
    selectedOpportunity?.id,
    selectedOpportunity?.probability,
  ]);

  const isRecurring = formData.cadence !== DEMAND_FORECAST_CADENCE.ONE_TIME;

  const title = useMemo(() => {
    if (readOnly) {
      return "Forecast";
    }
    return mode === "create" ? "New Forecast" : "Edit Forecast";
  }, [mode, readOnly]);

  const handleSelectOpportunity = (opportunityId: string | null) => {
    const opportunity = opportunityId
      ? opportunities.get(opportunityId)
      : undefined;
    setFormData({ ...formData, opportunityId });
    setOpportunityProbabilityDraft(
      opportunity ? String(opportunity.probability) : "",
    );
  };

  const getOpportunityProbabilityValidationError = (): string | null => {
    if (!formData.opportunityId || !selectedOpportunity) {
      return null;
    }
    const trimmedProbability = opportunityProbabilityDraft.trim();
    if (!trimmedProbability) {
      return "Opportunity probability is required";
    }

    const probability = Number(trimmedProbability);
    if (
      !Number.isInteger(probability) ||
      probability < MIN_OPPORTUNITY_PROBABILITY ||
      probability > MAX_OPPORTUNITY_PROBABILITY
    ) {
      return `Opportunity probability must be between ${MIN_OPPORTUNITY_PROBABILITY} and ${MAX_OPPORTUNITY_PROBABILITY}`;
    }
    return null;
  };

  const getParsedOpportunityProbability = (): number | null => {
    if (!formData.opportunityId || !selectedOpportunity) {
      return null;
    }
    return Number(opportunityProbabilityDraft.trim());
  };

  const validate = (): string | null => {
    if (!formData.opportunityId) {
      return "Opportunity is required";
    }
    if (!formData.description.trim()) {
      return "Description is required";
    }
    if (
      !formData.quantityPerPeriod ||
      Number(formData.quantityPerPeriod) <= 0
    ) {
      return "Quantity per period must be greater than zero";
    }
    if (!formData.unitPrice || Number(formData.unitPrice) < 0) {
      return "Unit price must be zero or greater";
    }
    if (!formData.startDate) {
      return "Start date is required";
    }
    if (
      formData.endDate &&
      parseDateInput(formData.endDate) < parseDateInput(formData.startDate)
    ) {
      return "End date must be on or after start date";
    }
    const opportunityProbabilityError =
      getOpportunityProbabilityValidationError();
    if (opportunityProbabilityError) {
      return opportunityProbabilityError;
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        opportunityId: formData.opportunityId!,
        locationId: formData.locationId,
        itemId: formData.itemId,
        description: formData.description.trim(),
        reasoning: formData.reasoning.trim() || null,
        quantityPerPeriod: formData.quantityPerPeriod,
        quantityUnit: formData.quantityUnit,
        unitPrice: formData.unitPrice,
        cadence: formData.cadence,
        startDate: parseDateInput(formData.startDate),
        endDate: formData.endDate ? parseDateInput(formData.endDate) : null,
      };
      const opportunityProbability = getParsedOpportunityProbability();

      if (
        formData.opportunityId &&
        opportunityProbability !== null &&
        opportunityProbability !== selectedOpportunity?.probability
      ) {
        const { error } = await updateOpportunity(formData.opportunityId, {
          probability: opportunityProbability,
        });
        if (error) {
          toast.error(error);
          return;
        }
      }

      const result = await onSave(payload);
      if (result === true) {
        toast.success(
          mode === "create" ? "Forecast created" : "Forecast updated",
        );
        onOpenChange(false);
      } else {
        toast.error(result);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            Expected demand by period for one opportunity.
          </SheetDescription>
        </SheetHeader>

        <fieldset
          disabled={readOnly}
          className="space-y-4 py-4 disabled:opacity-100"
        >
          <div className="divide-y">
            <ForecastFormRow label="Opportunity" required wide>
              <OpportunitySelector
                currentlySelectedId={formData.opportunityId}
                onSelectId={handleSelectOpportunity}
                disabled={!!fixedOpportunityId}
              />
            </ForecastFormRow>

            {formData.opportunityId && (
              <ForecastFormRow
                label="Opp %"
                htmlFor="forecast-opportunity-probability"
              >
                <div className="flex items-center justify-end gap-2">
                  <Input
                    id="forecast-opportunity-probability"
                    type="number"
                    min={MIN_OPPORTUNITY_PROBABILITY}
                    max={MAX_OPPORTUNITY_PROBABILITY}
                    step={1}
                    value={opportunityProbabilityDraft}
                    onChange={(event) =>
                      setOpportunityProbabilityDraft(event.target.value)
                    }
                    disabled={!selectedOpportunity}
                    placeholder={selectedOpportunity ? "0" : "Loading"}
                    className="w-24 text-right tabular-nums"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </ForecastFormRow>
            )}

            <ForecastFormRow label="Location" wide>
              <LocationSelector
                currentlySelectedId={formData.locationId}
                onSelectId={(locationId) =>
                  setFormData({ ...formData, locationId })
                }
              />
            </ForecastFormRow>

            <ForecastFormRow label="Item" wide>
              <ItemSelector
                currentlySelectedId={formData.itemId}
                onSelectId={(itemId) =>
                  setFormData({
                    ...formData,
                    itemId,
                    quantityUnit: itemId
                      ? (items.get(itemId)?.unit ?? formData.quantityUnit)
                      : formData.quantityUnit,
                  })
                }
              />
            </ForecastFormRow>

            <div className="space-y-1.5 py-2.5">
              <Label
                htmlFor="forecast-description"
                className="text-sm font-medium leading-tight"
              >
                Description<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Textarea
                id="forecast-description"
                value={formData.description}
                onChange={(event) =>
                  setFormData({ ...formData, description: event.target.value })
                }
                rows={3}
                placeholder="SKU, customer program, or demand note"
                className="min-h-[90px]"
              />
            </div>

            <div className="space-y-1.5 py-2.5">
              <Label
                htmlFor="forecast-reasoning"
                className="text-sm font-medium leading-tight"
              >
                Reasoning
              </Label>
              <Textarea
                id="forecast-reasoning"
                value={formData.reasoning}
                onChange={(event) =>
                  setFormData({ ...formData, reasoning: event.target.value })
                }
                rows={3}
                placeholder="Why this forecast exists, what source it came from, or what the agent inferred"
                className="min-h-[90px]"
              />
            </div>

            <ForecastFormRow
              label="Quantity / period"
              htmlFor="forecast-quantity"
              required
            >
              <Input
                id="forecast-quantity"
                type="number"
                min="0"
                step="0.01"
                value={formData.quantityPerPeriod}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    quantityPerPeriod: event.target.value,
                  })
                }
              />
            </ForecastFormRow>

            <ForecastFormRow label="Unit" htmlFor="forecast-unit" required>
              <Select
                value={formData.quantityUnit}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    quantityUnit: value as ItemUnit,
                  })
                }
              >
                <SelectTrigger id="forecast-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {itemUnitOptions.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {ITEM_UNIT_LABEL[unit]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ForecastFormRow>

            <ForecastFormRow
              label="Unit price"
              htmlFor="forecast-unit-price"
              required
            >
              <CurrencyInput
                id="forecast-unit-price"
                value={formData.unitPrice}
                onChange={(value) =>
                  setFormData({ ...formData, unitPrice: value || "0" })
                }
                prefix="$"
              />
            </ForecastFormRow>

            <ForecastFormRow label="Cadence" required>
              <Select
                value={formData.cadence}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    cadence: value as DemandForecastCadence,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEMAND_FORECAST_CADENCE_VALUES.map((cadence) => (
                    <SelectItem key={cadence} value={cadence}>
                      {DEMAND_FORECAST_CADENCE_DISPLAY_NAMES[cadence]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ForecastFormRow>

            <ForecastFormRow label="Start" htmlFor="forecast-start" required>
              <Input
                id="forecast-start"
                type="date"
                value={formData.startDate}
                onChange={(event) =>
                  setFormData({ ...formData, startDate: event.target.value })
                }
              />
            </ForecastFormRow>

            <ForecastFormRow label="End" htmlFor="forecast-end">
              <Input
                id="forecast-end"
                type="date"
                value={formData.endDate}
                onChange={(event) =>
                  setFormData({ ...formData, endDate: event.target.value })
                }
                disabled={!isRecurring}
              />
            </ForecastFormRow>
          </div>
        </fieldset>

        <SheetFooter className="border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "create" ? "Create Forecast" : "Save Changes"}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
